import React, { useEffect, useState } from "react";
import { Alert, Box, TextField, Typography } from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import PrintIcon from "@mui/icons-material/Print";

import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
  IpdDetailRow,
  IpdSectionCard,
} from "../../ipd/utils";
import { MlcBadge } from "./EmergencyBadges";
import { formatDateTime } from "../constants";
import { setMlcFlag, updateMlcDetail } from "../api/emergencyApi";
import { detailBlock, printDocument } from "../utils/print";

const ATTENDANT_FIELDS = [
  ["attendant_name", "Name", "Who brought the patient in"],
  ["attendant_relationship", "Relationship", "e.g. Brother, Bystander"],
  ["attendant_contact", "Contact number", ""],
];

/**
 * Medico-legal case (ER-MLC-*).
 *
 * Three things staff need here and nowhere else: the flag and its case number,
 * who brought the patient in, and the record of the police having been told.
 *
 * The wording around removing the flag is load-bearing — the case number is
 * kept and restored on re-flagging, and someone removing a flag they applied by
 * mistake needs to know they have not destroyed a legal identifier.
 */
export default function MedicoLegalCard({ visit, onChanged, disabled }) {
  const mlc = visit.mlc || null;
  const isFlagged = Boolean(visit.is_mlc);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editingAttendant, setEditingAttendant] = useState(false);
  const [attendant, setAttendant] = useState({});
  const [officer, setOfficer] = useState("");
  const [buckle, setBuckle] = useState("");
  const [station, setStation] = useState("");

  useEffect(() => {
    setAttendant({
      attendant_name: mlc?.attendant_name || "",
      attendant_relationship: mlc?.attendant_relationship || "",
      attendant_contact: mlc?.attendant_contact || "",
    });
    setOfficer(mlc?.police_officer_name || "");
    setBuckle(mlc?.police_officer_buckle_no || "");
    setStation(mlc?.police_station || "");
  }, [mlc]);

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setError(e?.message || "That did not work");
    } finally {
      setBusy(false);
    }
  };

  const printIntimation = () => {
    const opened = printDocument({
      title: "Police Intimation Report",
      subtitle: `Medico-legal case ${mlc.case_number}`,
      body: `
        <h2>Case identifiers</h2>
        ${detailBlock([
          ["Medico-legal case number", mlc.case_number],
          ["Patient", visit.display_name],
          ["Visit number", visit.visit_number],
          ["Hospital identifier", visit.patient_id],
          ["Age / gender", `${visit.age ?? "not recorded"} / ${visit.gender || "—"}`],
          ["Chief complaint", visit.chief_complaint],
          ["Arrived", formatDateTime(visit.arrived_at)],
        ])}
        <h2>Attendant</h2>
        ${detailBlock([
          ["Name", mlc.attendant_name],
          ["Relationship to patient", mlc.attendant_relationship],
          ["Contact number", mlc.attendant_contact],
        ]) || "<p>Not recorded.</p>"}
        <h2>Intimation</h2>
        ${detailBlock([
          ["Officer informed", mlc.police_officer_name],
          ["Buckle number", mlc.police_officer_buckle_no],
          ["Police station", mlc.police_station],
          ["Informed at", formatDateTime(mlc.police_informed_at)],
          ["Recorded by", mlc.police_recorded_by_name],
        ])}
        <p class="note">
          This document records a notification made by telephone or in person.
          No electronic message was sent to any police system.
        </p>`,
    });
    if (!opened) setError("The print window was blocked — allow pop-ups for this site.");
  };

  /* ------------------------------------------------ not a medico-legal case */
  if (!isFlagged) {
    return (
      <IpdSectionCard
        title="Medico-legal"
        subtitle="Assault, road accident, poisoning, or brought in dead"
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
            {error}
          </Alert>
        )}
        <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
          Not flagged as a medico-legal case. Flagging stamps a unique case number the
          moment it is applied, and can be done at any point in the visit.
          {mlc?.case_number && (
            <>
              {" "}This visit was previously flagged as{" "}
              <strong>{mlc.case_number}</strong>; re-flagging restores that same number.
            </>
          )}
        </Typography>
        {!disabled && (
          <IpdButton
            startIcon={<GavelIcon />}
            loading={busy}
            onClick={() => run(() => setMlcFlag(visit.id, true))}
          >
            Flag as medico-legal
          </IpdButton>
        )}
      </IpdSectionCard>
    );
  }

  /* ---------------------------------------------------------------- flagged */
  return (
    <IpdSectionCard
      title="Medico-legal case"
      subtitle={mlc?.flagged_at ? `Flagged ${formatDateTime(mlc.flagged_at)}` : undefined}
      actions={<MlcBadge caseNumber={mlc?.case_number} />}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}>
          {error}
        </Alert>
      )}

      <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mb: 2.5 }}>
        The case number is generated once and never reissued — it survives the flag being
        removed and re-applied.
      </Typography>

      {/* Attendant ------------------------------------------------------- */}
      <Typography sx={{ ...ipdLabelSx, mb: 1 }}>Attendant</Typography>
      {editingAttendant ? (
        <Box sx={{ display: "grid", gap: 1.5, mb: 2 }}>
          {ATTENDANT_FIELDS.map(([key, label, placeholder]) => (
            <TextField
              key={key}
              value={attendant[key] || ""}
              onChange={(e) => setAttendant((a) => ({ ...a, [key]: e.target.value }))}
              label={label}
              placeholder={placeholder}
              size="small"
              fullWidth
              sx={ipdElevatedInputSx}
              inputProps={{ "aria-label": label }}
            />
          ))}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <IpdButton variant="outlined" size="small" onClick={() => setEditingAttendant(false)}>
              Cancel
            </IpdButton>
            <IpdButton
              size="small"
              loading={busy}
              onClick={() =>
                run(async () => {
                  await updateMlcDetail(visit.id, attendant);
                  setEditingAttendant(false);
                })
              }
            >
              Save attendant
            </IpdButton>
          </Box>
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          {mlc?.attendant_name ? (
            <>
              <IpdDetailRow label="Name" value={mlc.attendant_name} />
              {mlc.attendant_relationship && (
                <IpdDetailRow label="Relationship" value={mlc.attendant_relationship} />
              )}
              {mlc.attendant_contact && (
                <IpdDetailRow label="Contact" value={mlc.attendant_contact} />
              )}
            </>
          ) : (
            <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
              Not recorded.
            </Typography>
          )}
          {!disabled && (
            <IpdButton
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
              onClick={() => setEditingAttendant(true)}
            >
              {mlc?.attendant_name ? "Edit attendant" : "Record attendant"}
            </IpdButton>
          )}
        </Box>
      )}

      {/* Police intimation ------------------------------------------------ */}
      <Typography sx={{ ...ipdLabelSx, mb: 1 }}>Police intimation</Typography>
      {mlc?.police_informed_at ? (
        <Box
          sx={{
            px: 1.75, py: 1.25, mb: 1.5,
            bgcolor: IPD_COLORS.success.bg,
            border: `1px solid ${IPD_COLORS.success.border}`,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
          }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: IPD_COLORS.success.text }}>
            Police informed
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textPrimary }}>
            {mlc.police_officer_name}
            {mlc.police_officer_buckle_no ? ` (Buckle ${mlc.police_officer_buckle_no})` : ""}
            , {mlc.police_station} · {formatDateTime(mlc.police_informed_at)}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "grid", gap: 1.5, mb: 1.5 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
            <TextField
              value={officer}
              onChange={(e) => setOfficer(e.target.value)}
              label="Officer's name"
              size="small"
              disabled={disabled}
              sx={ipdElevatedInputSx}
              inputProps={{ "aria-label": "Officer's name" }}
            />
            <TextField
              value={buckle}
              onChange={(e) => setBuckle(e.target.value)}
              label="Officer buckle no."
              size="small"
              disabled={disabled}
              sx={ipdElevatedInputSx}
              inputProps={{ "aria-label": "Officer buckle number" }}
            />
          </Box>
          <TextField
            value={station}
            onChange={(e) => setStation(e.target.value)}
            label="Police station"
            size="small"
            fullWidth
            disabled={disabled}
            sx={ipdElevatedInputSx}
            inputProps={{ "aria-label": "Police station" }}
          />
          <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
            The buckle number is required — every recorded intimation must name an
            accountable officer.
          </Typography>
          {!disabled && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <IpdButton
                size="small"
                loading={busy}
                disabled={!officer.trim() || !buckle.trim() || !station.trim()}
                onClick={() =>
                  run(() =>
                    updateMlcDetail(visit.id, {
                      police_officer_name: officer.trim(),
                      police_officer_buckle_no: buckle.trim(),
                      police_station: station.trim(),
                    })
                  )
                }
              >
                Record intimation
              </IpdButton>
            </Box>
          )}
        </Box>
      )}

      <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted, mb: 2 }}>
        This documents a call already made by phone or in person. Nothing is sent to any
        police system.
      </Typography>

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        {mlc?.police_informed_at && (
          <IpdButton variant="outlined" size="small" startIcon={<PrintIcon />} onClick={printIntimation}>
            Print intimation report
          </IpdButton>
        )}
        {!disabled && (
          <IpdButton
            variant="outlined"
            size="small"
            tone="red"
            loading={busy}
            onClick={() => run(() => setMlcFlag(visit.id, false))}
          >
            Remove flag
          </IpdButton>
        )}
      </Box>
    </IpdSectionCard>
  );
}
